import ResourceTyped from 'Resources/ResourceTyped';

export type AgentRunStatus =
	| 'pending'
	| 'running'
	| 'awaiting_approval'
	| 'awaiting_input'
	| 'succeeded'
	| 'failed'
	| 'cancelled';

export type AgentRun = {
	id: number;
	agentId: number;
	taskId: number | null;
	conversationId: number | null;
	onBehalfOfUserId: number;
	status: AgentRunStatus;
	startedAt: string | null;
	finishedAt: string | null;
	contextSnapshot: Record<string, unknown> | null;
	sessionStateBefore: Record<string, unknown> | null;
	sessionStateAfter: Record<string, unknown> | null;
	inputTokens: number;
	outputTokens: number;
	cachedInputTokens: number;
	cacheCreationTokens: number;
	costCents: number;
	/** Customer-facing credits (1 credit = 1 SEK), computed server-side from
	 *  costCents via the scope's USD→SEK rate + margin. See WEB-6797. */
	costCredits: number;
	livenessState: string | null;
	error: string | null;
	errorCode: string | null;
	scheduledRetryAt: string | null;
	scheduledRetryAttempt: number;
	wakeupRequestId: number | null;
	/** Provenance signals (WEB-7089 audit view). Populated unevenly across
	 *  producers, so the audit derives a label from whichever is present and
	 *  falls back gracefully. */
	kind: string | null;
	parentRunId: number | null;
	workflowId: number | null;
	feedbackScore: number | null;
	feedbackReason: string | null;
	regDate: string;
};

export type AgentRunEvent = {
	id: number;
	runId: number;
	seq: number;
	eventType: string;
	payload: Record<string, unknown> | null;
	regDate: string;
};

export type AgentRunListFilters = {
	limit?: number;
	offset?: number;
	agentId?: number;
	taskId?: number;
	conversationId?: number;
	status?: AgentRunStatus | AgentRunStatus[];
	/** WEB-7089 — admins request the org-wide view (all users' runs). Ignored
	 *  for non-admins server-side, so passing it is safe. */
	admin?: boolean;
	/** Inclusive ISO date bounds on startedAt (WEB-7089 audit filter). */
	from?: string;
	to?: string;
};

class AgentRuns extends ResourceTyped<AgentRun> {
	constructor() {
		super('agent-runs');
	}

	async list(
		filters: AgentRunListFilters = {}
	): Promise<{ data: AgentRun[]; metadata: { total: number; offset: number; limit: number } }> {
		const params = new URLSearchParams();
		if (filters.limit != null) params.set('limit', String(filters.limit));
		if (filters.offset != null) params.set('offset', String(filters.offset));
		if (filters.agentId != null) params.set('agentId', String(filters.agentId));
		if (filters.taskId != null) params.set('taskId', String(filters.taskId));
		if (filters.conversationId != null) params.set('conversationId', String(filters.conversationId));
		if (filters.status) {
			const v = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
			params.set('status', v);
		}
		if (filters.admin) params.set('admin', 'true');
		if (filters.from) params.set('from', filters.from);
		if (filters.to) params.set('to', filters.to);
		const q = params.toString();
		const res = await fetch(`/api/v2/agent-runs${q ? `?${q}` : ''}`);
		if (!res.ok) throw new Error(`Failed to list runs (${res.status})`);
		return res.json();
	}

	async listEvents(
		runId: number,
		opts: { limit?: number; offset?: number } = {}
	): Promise<AgentRunEvent[]> {
		const params = new URLSearchParams();
		if (opts.limit != null) params.set('limit', String(opts.limit));
		if (opts.offset != null) params.set('offset', String(opts.offset));
		const q = params.toString();
		const res = await fetch(`/api/v2/agent-runs/${runId}/events${q ? `?${q}` : ''}`);
		if (!res.ok) throw new Error(`Failed to list events (${res.status})`);
		const body = await res.json();
		return body.data ?? [];
	}
}

const resource = new AgentRuns();
export default resource;
